import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  Users, MapPin, Camera, Plus, Edit, Trash2, UserPlus, Settings,
  Shield, Bell, Eye, AlertTriangle, CheckCircle, Clock, Search,
  Filter, MoreVertical, X, Check, UserCheck, UserX, Building, Key,
  ArrowRight, Globe, ShieldCheck, Activity, Terminal
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Shared Glass Components ---

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: any; headerAction?: React.ReactNode }> = ({ children, className = '', title, icon: Icon, headerAction }) => (
  <div className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 ${className}`}>
    {(title || Icon || headerAction) && (
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-400" />}
          {title && <h3 className="text-sm font-bold tracking-tight text-blue-100/90 uppercase pr-4">{title}</h3>}
        </div>
        {headerAction}
      </div>
    )}
    <div className="p-6">
      {children}
    </div>
  </div>
);

const StatCard: React.FC<{ label: string; value: string; icon: any; color: string; bgColor: string }> = ({ label, value, icon: Icon, color, bgColor }) => (
  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 relative overflow-hidden group hover:bg-white/10 transition-all">
    <div className={`absolute top-0 right-0 w-24 h-24 ${bgColor} blur-[40px] opacity-20 -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700`} />
    <div className="flex items-center gap-4 relative z-10">
      <div className={`w-12 h-12 rounded-xl ${bgColor} border border-white/10 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">{label}</p>
        <h4 className="text-2xl font-black text-white">{value}</h4>
      </div>
    </div>
  </div>
);

// --- Sub-Components ---

const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }> = ({ isOpen, onClose, title, children }) => (
  <AnimatePresence>
    {isOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }} 
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm" 
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-[#0f111a] border border-white/10 rounded-3xl w-full max-w-lg shadow-2xl relative z-10 overflow-hidden"
        >
          <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
            <h3 className="text-lg font-black tracking-tight text-white uppercase">{title}</h3>
            <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-xl transition-colors">
              <X className="w-5 h-5 text-blue-100/40" />
            </button>
          </div>
          <div className="p-8">
            {children}
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

// --- Main Management Component ---

export function Management() {
  const [activeTab, setActiveTab] = useState('locations');
  const [loading, setLoading] = useState(false);
  
  // State
  const [overview, setOverview] = useState({ subusers: 0, locations: 0, cameras: 0, pendingRequests: 0 });
  const [locations, setLocations] = useState<any[]>([]);
  const [subUsers, setSubUsers] = useState<any[]>([]);
  const [accessRequests, setAccessRequests] = useState<any[]>([]);
  const [availableCameras, setAvailableCameras] = useState<any[]>([]);
  
  // Modals
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showSubUserModal, setShowSubUserModal] = useState(false);
  const [newLocation, setNewLocation] = useState({ name: '', description: '', address: '' });
  const [newSubUser, setNewSubUser] = useState({ email: '', name: '', access_type: 'camera', permissions: [] as string[] });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('csense_auth_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [ovRes, locRes, camRes, reqRes] = await Promise.all([
        fetch('/api/management/overview', { headers }),
        fetch('/api/management/locations', { headers }),
        fetch('/api/cameras', { headers }),
        fetch('/api/management/access-requests', { headers })
      ]);

      if (ovRes.ok) setOverview(await ovRes.json());
      if (locRes.ok) setLocations(await locRes.json());
      if (camRes.ok) {
        const data = await camRes.json();
        setAvailableCameras(data.cameras || []);
      }
      if (reqRes.ok) setAccessRequests(await reqRes.json());
      
      // Fetch subusers
      const meRes = await fetch('/api/auth/me', { headers });
      if (meRes.ok) {
        const me = await meRes.json();
        const subRes = await fetch(`/api/subusers/${me.id}`, { headers });
        if (subRes.ok) {
          const data = await subRes.json();
          setSubUsers(data.subusers || []);
        }
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateLocation = async () => {
    if (!newLocation.name.trim()) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('csense_auth_token');
      const res = await fetch('/api/management/locations', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newLocation)
      });
      if (res.ok) {
        setShowLocationModal(false);
        setNewLocation({ name: '', description: '', address: '' });
        fetchData();
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleCreateSubUser = async () => {
    if (!newSubUser.email.trim() || !newSubUser.name.trim()) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('csense_auth_token');
      const meRes = await fetch('/api/auth/me', { headers: { 'Authorization': `Bearer ${token}` } });
      const me = await meRes.json();
      const res = await fetch(`/api/subusers/create/${me.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newSubUser)
      });
      if (res.ok) {
        setShowSubUserModal(false);
        setNewSubUser({ email: '', name: '', access_type: 'camera', permissions: [] });
        fetchData();
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleDeleteLocation = async (id: string) => {
    if (!confirm('Permanent deletion of this sector?')) return;
    const token = localStorage.getItem('csense_auth_token');
    await fetch(`/api/management/locations/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    fetchData();
  };

  const handleApproveRequest = async (id: string) => {
    const token = localStorage.getItem('csense_auth_token');
    await fetch(`/api/management/access-requests/${id}/approve`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
    fetchData();
  };

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-6 space-y-8 pb-32">
      {/* Dynamic Background Atmosphere */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/5 blur-[100px]" />
      </div>

      <div className="relative z-10 space-y-8">
        <div>
          <h1 className="text-3xl font-black tracking-tighter uppercase">Infrastructure Management</h1>
          <p className="text-sm text-blue-100/40 mt-1 font-medium tracking-wide">Orchestrate locations, cognitive nodes, and security protocols.</p>
        </div>

        {/* Global Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard label="Total Nodes" value={overview.cameras.toString()} icon={Camera} color="text-blue-400" bgColor="bg-blue-500/10" />
          <StatCard label="Active Sectors" value={overview.locations.toString()} icon={MapPin} color="text-purple-400" bgColor="bg-purple-500/10" />
          <StatCard label="Verified Personnel" value={overview.subusers.toString()} icon={Users} color="text-emerald-400" bgColor="bg-emerald-500/10" />
          <StatCard label="Triage Queue" value={overview.pendingRequests.toString()} icon={Bell} color="text-amber-400" bgColor="bg-amber-500/10" />
        </div>

        {/* Tactical Navigation */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-1 shadow-2xl">
            {[
              { id: 'locations', label: 'Sectors', icon: MapPin },
              { id: 'subusers', label: 'Personnel', icon: Users },
              { id: 'requests', label: 'Sync Queue', icon: Activity, count: overview.pendingRequests }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-all relative ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'text-blue-100/40 hover:text-white hover:bg-white/5'}`}
              >
                <tab.icon className="w-4 h-4" /> 
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-[8px] flex items-center justify-center rounded-full border-2 border-[#0a0b14]">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {activeTab === 'locations' && (
              <Button onClick={() => setShowLocationModal(true)} className="bg-blue-600 hover:bg-blue-500 text-white font-black px-6 py-6 rounded-2xl shadow-xl transition-all hover:scale-105 active:scale-95">
                <Plus className="w-5 h-5 mr-2" /> Add Sector
              </Button>
            )}
            {activeTab === 'subusers' && (
              <Button onClick={() => setShowSubUserModal(true)} className="bg-blue-600 hover:bg-blue-500 text-white font-black px-6 py-6 rounded-2xl shadow-xl">
                <UserPlus className="w-5 h-5 mr-2" /> Enlist Personnel
              </Button>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="grid grid-cols-1 gap-8">
          {activeTab === 'locations' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {locations.map((loc, i) => (
                <GlassPanel key={i} className="group hover:border-blue-500/30">
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center border border-white/5">
                      <Building className="w-6 h-6 text-blue-400" />
                    </div>
                    <button onClick={() => handleDeleteLocation(loc.id)} className="p-2 text-red-500/20 hover:text-red-500 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <h3 className="text-xl font-black text-white group-hover:text-blue-400 transition-colors uppercase tracking-tight">{loc.name}</h3>
                  <p className="text-xs text-blue-100/40 mt-2 font-medium leading-relaxed line-clamp-2">{loc.description || 'Secure Sector Authorization active.'}</p>
                  
                  <div className="mt-8 flex items-center justify-between border-t border-white/5 pt-6">
                    <div className="flex items-center gap-2">
                       <Camera className="w-3.5 h-3.5 text-blue-500/60" />
                       <span className="text-[10px] font-black text-blue-100/60 uppercase">{loc.cameraCount || 0} Nodes Connected</span>
                    </div>
                    <div className="text-[9px] font-mono text-blue-100/20">ID: {loc.id.slice(0,8)}</div>
                  </div>
                </GlassPanel>
              ))}
              {locations.length === 0 && (
                <div className="col-span-full py-40 text-center border-2 border-dashed border-white/5 rounded-[40px]">
                   <MapPin className="w-16 h-16 text-blue-500/10 mx-auto mb-6" />
                   <p className="text-blue-100/20 text-sm font-light italic">No sectors initialized in the current matrix.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'subusers' && (
            <GlassPanel title="Active Personnel" icon={Users}>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="pb-4 text-[10px] uppercase tracking-widest text-blue-100/40 font-black">Identity</th>
                      <th className="pb-4 text-[10px] uppercase tracking-widest text-blue-100/40 font-black">Access Layer</th>
                      <th className="pb-4 text-[10px] uppercase tracking-widest text-blue-100/40 font-black">Status</th>
                      <th className="pb-4 text-[10px] uppercase tracking-widest text-blue-100/40 font-black text-right">Synchronization</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {subUsers.map((user, i) => (
                      <tr key={i} className="group hover:bg-white/[0.02] transition-colors">
                        <td className="py-6">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center font-black text-xs text-blue-200">
                              {(user.name || user.first_name || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-white">{user.name || user.first_name || 'Classified'}</p>
                              <p className="text-[10px] text-blue-100/30 font-medium">{user.email || 'Encrypted'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-6">
                          <Badge variant="outline" className="bg-white/5 border-white/10 text-[9px] font-black uppercase text-blue-400 px-3 tracking-tighter">
                            {user.access_type || 'STANDARD'} LAYER
                          </Badge>
                        </td>
                        <td className="py-6">
                           <div className="flex items-center gap-2">
                             <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]" />
                             <span className="text-[10px] font-bold text-blue-100/60 uppercase">AUTHORIZED</span>
                           </div>
                        </td>
                        <td className="py-6 text-right">
                          <p className="text-[10px] font-mono text-blue-100/20 uppercase">Last Link: {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'NEVER'}</p>
                        </td>
                      </tr>
                    ))}
                    {subUsers.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-20 text-center text-blue-100/20 italic font-light">No personnel data synchronized.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </GlassPanel>
          )}

          {activeTab === 'requests' && (
            <div className="space-y-6">
              {accessRequests.map((req, i) => (
                <GlassPanel key={i} className="border-l-4 border-l-blue-600">
                  <div className="flex flex-wrap items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                       <div className="w-14 h-14 rounded-2xl bg-blue-600/10 flex items-center justify-center border border-blue-500/20">
                          <Terminal className="w-7 h-7 text-blue-400" />
                       </div>
                       <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-black text-white uppercase">{req.requesterName}</h3>
                            <Badge className="bg-blue-600 text-[9px] font-black">SYNC REQUEST</Badge>
                          </div>
                          <p className="text-xs text-blue-100/40 font-medium">Link attempt from: <span className="text-blue-100/60 font-mono italic">{req.requesterUsername}</span></p>
                       </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                       <Button onClick={() => handleApproveRequest(req.id)} className="bg-emerald-600 hover:bg-emerald-500 text-white font-black rounded-xl px-6 py-5">
                          <ShieldCheck className="w-4 h-4 mr-2" /> Authorize
                       </Button>
                       <Button variant="outline" className="border-white/10 text-white/40 hover:bg-white/5 rounded-xl px-6 py-5">
                          <X className="w-4 h-4 mr-2" /> Deny
                       </Button>
                    </div>
                  </div>
                  {req.message && (
                    <div className="mt-4 p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                       <p className="text-xs text-blue-100/60 leading-relaxed font-light italic">"{req.message}"</p>
                    </div>
                  )}
                </GlassPanel>
              ))}
              {accessRequests.length === 0 && (
                <div className="py-40 text-center border-2 border-dashed border-white/5 rounded-[40px]">
                   <Shield className="w-16 h-16 text-blue-500/10 mx-auto mb-6" />
                   <p className="text-blue-100/20 text-sm font-light italic">System pulse stable. No pending synchronization requests.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <Modal isOpen={showLocationModal} onClose={() => setShowLocationModal(false)} title="Initialize New Sector">
        <div className="space-y-6">
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase text-blue-100/40 tracking-widest pl-1">Sector Designation</Label>
            <Input 
              value={newLocation.name} 
              onChange={e => setNewLocation({...newLocation, name: e.target.value})} 
              placeholder="e.g. ALPHA-CENTAURI" 
              className="bg-white/5 border-white/10 rounded-xl py-6 px-4 text-white placeholder:text-white/10 focus:ring-blue-500/20" 
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase text-blue-100/40 tracking-widest pl-1">Tactical Description</Label>
            <Textarea 
              value={newLocation.description} 
              onChange={e => setNewLocation({...newLocation, description: e.target.value})} 
              placeholder="Detailed operational parameters..." 
              className="bg-white/5 border-white/10 rounded-xl min-h-[120px] text-white placeholder:text-white/10 focus:ring-blue-500/20" 
            />
          </div>
          <div className="pt-4 flex gap-3">
             <Button onClick={() => setShowLocationModal(false)} variant="outline" className="flex-1 py-6 border-white/10 text-blue-100/40 rounded-2xl">Abort</Button>
             <Button onClick={handleCreateLocation} disabled={loading} className="flex-[2] py-6 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl shadow-xl">
               {loading ? 'Processing...' : 'Initialize Matrix'}
             </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showSubUserModal} onClose={() => setShowSubUserModal(false)} title="Enlist Security Personnel">
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-blue-100/40 tracking-widest pl-1">Full Identity</Label>
              <Input 
                value={newSubUser.name} 
                onChange={e => setNewSubUser({...newSubUser, name: e.target.value})} 
                placeholder="Agent Name" 
                className="bg-white/5 border-white/10 rounded-xl py-6 px-4 text-white focus:ring-blue-500/20" 
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-blue-100/40 tracking-widest pl-1">Primary Comm Link (Email)</Label>
              <Input 
                type="email"
                value={newSubUser.email} 
                onChange={e => setNewSubUser({...newSubUser, email: e.target.value})} 
                placeholder="agent@matrix.io" 
                className="bg-white/5 border-white/10 rounded-xl py-6 px-4 text-white focus:ring-blue-500/20" 
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase text-blue-100/40 tracking-widest pl-1">Access Protocol</Label>
            <Select value={newSubUser.access_type} onValueChange={v => setNewSubUser({...newSubUser, access_type: v})}>
              <SelectTrigger className="bg-white/5 border-white/10 rounded-xl py-6 px-4 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f111a] border-white/10">
                <SelectItem value="camera">Node-Specific Authorization</SelectItem>
                <SelectItem value="location">Sector-Wide Authorization</SelectItem>
                <SelectItem value="usecase">Logic-Specific Authorization</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="pt-4 flex gap-3">
             <Button onClick={() => setShowSubUserModal(false)} variant="outline" className="flex-1 py-6 border-white/10 text-blue-100/40 rounded-2xl">Abort</Button>
             <Button onClick={handleCreateSubUser} disabled={loading} className="flex-[2] py-6 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl shadow-xl">
               {loading ? 'Decrypting...' : 'Finalize Enlistment'}
             </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}